/* ==========================================================================
   Brothers in Watches — Cinematic layer behaviour
   Scroll reveals, draggable product rails, rail progress.
   No dependencies. Safe to load with `defer`.
   ========================================================================== */
(function () {
  'use strict';

  // Build marker — survives Shopify's asset minification, so the running
  // version can be identified from the console.
  window.BW_CINEMATIC = 10;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     Scroll reveal

     A position sweep, deliberately NOT an IntersectionObserver.
     IntersectionObserver only fires when an element CROSSES a threshold.
     An element that moves from fully below the viewport to fully above it
     in a single jump — dragging the scrollbar, following an anchor link,
     or a scroll position the browser restores on reload — crosses nothing,
     so it never fires, and its content stays invisible for good. Comparing
     positions has no such blind spot, and for a dozen elements it costs
     nothing.

     The hidden state lives behind .bw-reveal-ready, which is only added
     here. If this script never runs, every .bw-reveal element stays fully
     visible rather than disappearing.
     ------------------------------------------------------------------ */

  var pending = [];
  var queued = false;
  var listening = false;

  function show(el, delay) {
    if (delay > 0) window.setTimeout(function () { el.classList.add('is-in'); }, delay);
    else el.classList.add('is-in');
  }

  function sweep() {
    queued = false;

    var limit = window.innerHeight * 0.88;   // reveal a little before the bottom edge
    var still = [];

    for (var i = 0; i < pending.length; i++) {
      var el = pending[i];
      var top = el.getBoundingClientRect().top;

      if (top < limit) {
        // Already scrolled past? Show it at once — a stagger would be a
        // pointless delay on content the visitor has moved beyond.
        show(el, top < 0 ? 0 : parseInt(el.getAttribute('data-bw-delay') || '0', 10));
      } else {
        still.push(el);
      }
    }

    pending = still;

    if (!pending.length && listening) {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      listening = false;
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(sweep);
  }

  function initReveal(root) {
    var targets = (root || document).querySelectorAll('.bw-reveal:not([data-bw-observed])');
    if (!targets.length) return;

    // No motion wanted, or a browser too old for this: show everything.
    if (reduced) {
      Array.prototype.forEach.call(targets, function (el) {
        el.setAttribute('data-bw-observed', '');
        el.classList.add('is-in');
      });
      return;
    }

    document.documentElement.classList.add('bw-reveal-ready');

    Array.prototype.forEach.call(targets, function (el) {
      el.setAttribute('data-bw-observed', '');
      pending.push(el);
    });

    if (!listening) {
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      listening = true;
    }

    schedule();
  }

  /* ------------------------------------------------------------------
     Rails
     Native scroll-snap does the work; this adds pointer dragging on
     desktop and keeps the progress bar in step. Touch scrolling is left
     entirely to the browser.
     ------------------------------------------------------------------ */
  function initRail(rail) {
    if (rail.hasAttribute('data-bw-rail-ready')) return;
    rail.setAttribute('data-bw-rail-ready', '');

    var wrap = rail.closest('[data-bw-rail-scope]') || rail.parentNode;
    var bar = wrap ? wrap.querySelector('.bw-rail-progress__bar') : null;

    function updateProgress() {
      if (!bar) return;
      var scrollable = rail.scrollWidth - rail.clientWidth;
      var ratio = rail.clientWidth / rail.scrollWidth;
      var width = Math.max(8, Math.min(100, ratio * 100));
      var pos = scrollable > 0 ? (rail.scrollLeft / scrollable) * (100 - width) : 0;
      bar.style.width = width + '%';
      bar.style.left = pos + '%';
    }

    rail.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
    updateProgress();

    /* Pointer drag — mouse and pen only. */
    var down = false, startX = 0, startScroll = 0, moved = 0;

    rail.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      down = true;
      moved = 0;
      startX = e.clientX;
      startScroll = rail.scrollLeft;
      rail.classList.add('is-dragging');
    });

    rail.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      moved = Math.abs(dx);
      if (moved > 3) e.preventDefault();
      rail.scrollLeft = startScroll - dx;
    });

    function release() {
      if (!down) return;
      down = false;
      rail.classList.remove('is-dragging');
    }

    rail.addEventListener('pointerup', release);
    rail.addEventListener('pointercancel', release);
    rail.addEventListener('pointerleave', release);

    /* A drag must not fire the card's link. */
    rail.addEventListener('click', function (e) {
      if (moved > 6) { e.preventDefault(); e.stopPropagation(); }
      moved = 0;
    }, true);
  }

  function initRails(root) {
    var rails = (root || document).querySelectorAll('.bw-rail');
    Array.prototype.forEach.call(rails, initRail);
  }

  /* ------------------------------------------------------------------
     Product gallery
     Thumbnails swap the stage image. Each thumb is a real <button>, so it
     is reachable by keyboard; without this script the stage simply shows
     the featured image and nothing is broken.
     ------------------------------------------------------------------ */
  function initGallery(root) {
    var galleries = (root || document).querySelectorAll('[data-bw-gallery]:not([data-bw-gallery-ready])');

    Array.prototype.forEach.call(galleries, function (g) {
      g.setAttribute('data-bw-gallery-ready', '');
      // Liquid's image_tag cannot take a hyphenated named argument, so the
      // stage is found by its class rather than a data attribute.
      var stage = g.querySelector('.bw-pdp__stage-img');
      var thumbs = g.querySelectorAll('[data-bw-thumb]');
      if (!stage || !thumbs.length) return;

      // The "+N" tile reveals the thumbs held back past the fifth, then
      // removes itself. Querying thumbs above already includes the hidden
      // ones, so they are wired for clicking before they are ever shown.
      var more = g.querySelector('[data-bw-more]');
      if (more) {
        more.addEventListener('click', function () {
          Array.prototype.forEach.call(
            g.querySelectorAll('.bw-pdp__thumb.is-extra'),
            function (x) { x.classList.remove('is-extra'); }
          );
          more.setAttribute('aria-expanded', 'true');
          more.classList.add('is-done');
        });
      }

      Array.prototype.forEach.call(thumbs, function (t) {
        t.addEventListener('click', function () {
          var src = t.getAttribute('data-bw-thumb');
          if (!src) return;
          stage.removeAttribute('srcset');   // srcset would override src
          stage.removeAttribute('sizes');
          stage.src = src;
          Array.prototype.forEach.call(thumbs, function (o) {
            o.classList.remove('is-active');
            o.removeAttribute('aria-current');
          });
          t.classList.add('is-active');
          t.setAttribute('aria-current', 'true');
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Article page: reading progress, generated contents rail, copy link.

     The contents rail has to be built here rather than in Liquid: an
     article's body is stored rich text, so the h2s inside it are opaque to
     the template. Headings without an id get one derived from their text,
     and the rail is only revealed once there are at least two — a single
     heading is not a table of contents.
     ------------------------------------------------------------------ */
  function slugify(t) {
    return (t || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'section';
  }

  function initArticle(root) {
    var arts = (root || document).querySelectorAll('[data-bw-article]:not([data-bw-article-ready])');

    Array.prototype.forEach.call(arts, function (art) {
      art.setAttribute('data-bw-article-ready', '');

      /* --- contents ------------------------------------------------- */
      var body = art.querySelector('[data-bw-artbody]');
      var toc = art.querySelector('[data-bw-toc]');
      var list = art.querySelector('[data-bw-toclist]');
      var heads = [];

      if (body && list) {
        var hs = body.querySelectorAll('h2');
        var used = {};
        Array.prototype.forEach.call(hs, function (h) {
          var text = (h.textContent || '').trim();
          if (!text) return;
          var id = h.id;
          if (!id) {
            id = slugify(text);
            while (used[id] || document.getElementById(id)) { id = id + '-x'; }
            h.id = id;
          }
          used[id] = true;
          var a = document.createElement('a');
          a.href = '#' + id;
          a.textContent = text;
          list.appendChild(a);
          heads.push({ h: h, a: a });
        });
        if (heads.length >= 2 && toc) toc.hidden = false;
      }

      /* --- progress + active heading -------------------------------- */
      var bar = art.querySelector('[data-bw-progress]');
      var ticking = false;

      function measure() {
        ticking = false;
        if (bar && body) {
          var r = body.getBoundingClientRect();
          var total = r.height - window.innerHeight;
          var done = total > 0 ? (-r.top) / total : (r.top <= 0 ? 1 : 0);
          bar.style.width = Math.max(0, Math.min(1, done)) * 100 + '%';
        }
        if (heads.length) {
          var current = heads[0];
          for (var i = 0; i < heads.length; i++) {
            if (heads[i].h.getBoundingClientRect().top <= 120) current = heads[i];
          }
          heads.forEach(function (x) { x.a.classList.toggle('is-on', x === current); });
        }
      }
      function onScroll() { if (!ticking) { ticking = true; window.requestAnimationFrame(measure); } }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      measure();

      /* --- copy link ------------------------------------------------ */
      var copy = art.querySelector('[data-bw-copy]');
      if (copy) {
        copy.addEventListener('click', function () {
          var label = copy.querySelector('[data-bw-copylabel]');
          var original = label ? label.textContent : '';
          function done(ok) {
            if (!label) return;
            label.textContent = ok ? 'Link copied' : 'Press Ctrl+C';
            window.setTimeout(function () { label.textContent = original; }, 2400);
          }
          var url = window.location.href;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(function () { done(true); }, function () { done(false); });
          } else {
            done(false);
          }
        });
      }
    });
  }

  /* ------------------------------------------------------------------
     Click-to-play YouTube facade (PDP "On film").

     The markup ships a still image inside a plain link to the video, so with
     no JavaScript the band still works. Here the click is cancelled and the
     player is swapped in, which keeps the visitor on the product page and
     means YouTube's iframe — and its cookies — only load for someone who
     actually pressed play. youtube-nocookie is used for the same reason.

     initYouTubeLinks would otherwise give this anchor target="_blank", so it
     is skipped there by its data attribute.
     ------------------------------------------------------------------ */
  function initYouTubeEmbed(root) {
    var nodes = (root || document).querySelectorAll('[data-bw-yt]:not([data-bw-yt-ready])');

    Array.prototype.forEach.call(nodes, function (el) {
      el.setAttribute('data-bw-yt-ready', '');
      var id = el.getAttribute('data-bw-yt');
      if (!id) return;

      el.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return; // let people open it in a tab
        e.preventDefault();
        if (el.getAttribute('data-bw-yt-playing')) return;
        el.setAttribute('data-bw-yt-playing', '');

        var frame = document.createElement('iframe');
        frame.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
                    '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
        frame.title = el.getAttribute('aria-label') || 'Video';
        frame.setAttribute('frameborder', '0');
        frame.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        frame.setAttribute('allowfullscreen', '');
        frame.className = 'bw-pdp__filmframe';

        el.innerHTML = '';
        el.appendChild(frame);
        el.classList.add('is-playing');
      });
    });
  }

  /* ------------------------------------------------------------------
     YouTube links open in a new tab.

     The theme's own CTAs get target/rel from the bw-newtab snippet, which
     works without JavaScript. This covers what Liquid cannot reach: links
     typed into a product description or an article body, where the markup is
     stored rich text. Keyed on the URL, so it also catches anything added
     later. rel gets noopener so the opened tab cannot touch window.opener.
     ------------------------------------------------------------------ */
  function initYouTubeLinks(root) {
    var links = (root || document).querySelectorAll('a[href*="youtube.com"], a[href*="youtu.be"]');
    Array.prototype.forEach.call(links, function (a) {
      if (a.hasAttribute('data-bw-yt')) return;   // plays in place, not a new tab
      if (a.getAttribute('target') === '_blank') return;
      a.setAttribute('target', '_blank');
      var rel = (a.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
      if (rel.indexOf('noopener') === -1) rel.push('noopener');
      if (rel.indexOf('noreferrer') === -1) rel.push('noreferrer');
      a.setAttribute('rel', rel.join(' '));
    });
  }

  /* ------------------------------------------------------------------
     Sort select — submit on change. The form keeps a real submit button
     for the no-JavaScript case; CSS hides it once `js` is on <html>.
     ------------------------------------------------------------------ */
  function initAutoSubmit(root) {
    var sels = (root || document).querySelectorAll('[data-bw-autosubmit]:not([data-bw-submit-ready])');
    Array.prototype.forEach.call(sels, function (s) {
      s.setAttribute('data-bw-submit-ready', '');
      s.addEventListener('change', function () {
        if (s.form) s.form.submit();
      });
    });
  }

  /* ------------------------------------------------------------------
     Alt-text safety net
     An <img> with NO alt attribute is announced by screen readers as its
     filename, which is worse than silence. Shopify's image_tag omits the
     attribute entirely when the image has no alt set, so a third-party
     section rendering un-alted images leaves them bare — the Instagram
     strip does exactly this. Decorative images get alt="".

     This does not replace real alt text: any image with meaning should be
     given one in Shopify. It only stops the filename being read out.
     ------------------------------------------------------------------ */
  function initAltSafety(root) {
    var bare = (root || document).querySelectorAll('img:not([alt])');
    Array.prototype.forEach.call(bare, function (img) {
      img.setAttribute('alt', '');
    });
  }

  /* ------------------------------------------------------------------
     Boot, and re-boot inside the theme editor when a section is redrawn
     ------------------------------------------------------------------ */
  function boot(root) {
    initReveal(root);
    initRails(root);
    initGallery(root);
    initAutoSubmit(root);
    initAltSafety(root);
    initYouTubeLinks(root);
    initArticle(root);
    initYouTubeEmbed(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  // Images finishing late can change every element's position.
  window.addEventListener('load', schedule);

  // requestAnimationFrame does not run in a background tab, so a page
  // opened in one (cmd-click, 'open in new tab') has its sweep stalled
  // until it is looked at. Re-run it the moment the tab becomes visible.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) schedule();
  });

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) { boot(e.target); });
})();
